import { BulkActionsBar, Button, Card, useToast } from '@citizen-walk/shared-ui';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import {
  bulkModerate,
  deleteIdea,
  deleteInsight,
  setIdeaHidden,
  setInsightHidden,
  useInsightsModerationList,
  type BulkAction,
  type IdeaModerationItem,
  type InsightModerationItem,
  type ModerationCollection,
} from './useInsightsModeration';

/**
 * ECW Insight / Idea カードモデレーション画面 (admin 第 19 弾、Phase 5.5 REQ-4.3.42 / GAP-A13 MUST、A-S12 close)。
 *
 * 旧 v1 の `EcwModerationPage.tsx` (旧 admin app の Insight/Idea 一括モデレーション画面) を minimum scope で踏襲:
 *   - 2 タブ (Insight / Idea) で各 collection を別 table 表示
 *   - 各 card に対して: hidden toggle (公開 ↔ 非公開) + 物理削除 (window.confirm)
 *
 * AW10 (docs/108、old2 EcwModerationPage 完全踏襲):
 *   - High: 一括操作 (チェックボックス選択 + 全選択 + 一括 非表示/表示/削除 + 件数 confirm、shared-ui BulkActionsBar 再利用)
 *   - High: 行展開 (expandedId) で observations / diagnosis / actionIdea / llmAnalysis 全文 (whitespace-pre-wrap)
 *   - Medium: emotionTag / categoryLabel / llmAnalysis バッジ
 *   - Low: team / docID 表示
 *
 * posts-moderation (admin 第 11 弾) との差:
 *   - posts は 3 段階 visibility だが Insight/Idea は 公開/非公開 の 2 段階 (REQ-4.3.42)
 *
 * 認可: master / district admin (担当地区) のみ操作可。それ以外は rules で deny される (UI でも guard)。
 */

type Tab = 'insights' | 'ideas';

export function InsightsModerationPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const modState = useInsightsModerationList(districtId ?? null);
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>('insights');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'visible' | 'hidden'>('all');
  // AW10 High: 一括選択 ID + 行展開 ID + 一括処理中フラグ。
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canModerate = isMaster || isDistrictAdminOfThis;
  const uid = authState.user?.uid ?? '';

  const insights = modState.status === 'ready' ? modState.insights : [];
  const ideas = modState.status === 'ready' ? modState.ideas : [];

  const filteredInsights = useMemo(() => {
    if (filter === 'all') return insights;
    if (filter === 'hidden') return insights.filter((i) => i.hidden);
    return insights.filter((i) => !i.hidden);
  }, [insights, filter]);

  const filteredIdeas = useMemo(() => {
    if (filter === 'all') return ideas;
    if (filter === 'hidden') return ideas.filter((i) => i.hidden);
    return ideas.filter((i) => !i.hidden);
  }, [ideas, filter]);

  // 現在タブで表示中の項目 ID 配列 (全選択判定 / 一括対象に使用)。
  const currentItems: Array<InsightModerationItem | IdeaModerationItem> =
    tab === 'insights' ? filteredInsights : filteredIdeas;
  const currentCollection: ModerationCollection = tab === 'insights' ? 'ecwInsights' : 'ecwIdeas';

  // タブ / フィルタ変更時は選択と展開をリセット (old2 handleTabChange 踏襲)。
  useEffect(() => {
    setSelectedIds(new Set());
    setExpandedId(null);
  }, [tab, filter]);

  function toggleSelect(id: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(): void {
    setSelectedIds((prev) =>
      prev.size === currentItems.length && currentItems.length > 0
        ? new Set()
        : new Set(currentItems.map((r) => r.id)),
    );
  }

  function toggleExpand(id: string): void {
    setExpandedId((cur) => (cur === id ? null : id));
  }

  async function handleToggleInsight(item: InsightModerationItem): Promise<void> {
    if (!districtId || !canModerate) return;
    setBusyId(item.id);
    try {
      const next = !item.hidden;
      await setInsightHidden(districtId, item.id, next, uid);
      toast(
        'success',
        `気づきカード「${item.title || '(無題)'}」を${next ? '非公開' : '公開'}にしました`,
      );
    } catch (e) {
      toast('error', `更新失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleToggleIdea(item: IdeaModerationItem): Promise<void> {
    if (!districtId || !canModerate) return;
    setBusyId(item.id);
    try {
      const next = !item.hidden;
      await setIdeaHidden(districtId, item.id, next, uid);
      toast(
        'success',
        `処方箋カード「${item.title || '(無題)'}」を${next ? '非公開' : '公開'}にしました`,
      );
    } catch (e) {
      toast('error', `更新失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleDeleteInsight(item: InsightModerationItem): Promise<void> {
    if (!districtId || !canModerate) return;
    if (
      !confirm(
        `気づきカード「${item.title || '(無題)'}」を完全に削除します。復元できません。よろしいですか？`,
      )
    ) {
      return;
    }
    setBusyId(item.id);
    try {
      await deleteInsight(districtId, item.id);
      toast('success', '気づきカードを削除しました');
    } catch (e) {
      toast('error', `削除失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function handleDeleteIdea(item: IdeaModerationItem): Promise<void> {
    if (!districtId || !canModerate) return;
    if (
      !confirm(
        `処方箋カード「${item.title || '(無題)'}」を完全に削除します。復元できません。よろしいですか？`,
      )
    ) {
      return;
    }
    setBusyId(item.id);
    try {
      await deleteIdea(districtId, item.id);
      toast('success', '処方箋カードを削除しました');
    } catch (e) {
      toast('error', `削除失敗: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  // AW10 High: 一括操作 (件数 confirm → Promise.allSettled で並列処理 → 成功/失敗件数 toast)。
  async function handleBulk(action: BulkAction): Promise<void> {
    if (!districtId || !canModerate || selectedIds.size === 0) return;
    const label = action === 'delete' ? '完全削除' : action === 'hide' ? '非公開' : '公開に戻す';
    if (!confirm(`${selectedIds.size} 件を「${label}」にします。よろしいですか？`)) return;
    setBulkBusy(true);
    try {
      const { ok, fail } = await bulkModerate(
        districtId,
        currentCollection,
        [...selectedIds],
        action,
        uid,
      );
      setSelectedIds(new Set());
      toast(
        fail > 0 ? 'warning' : 'success',
        `${ok} 件処理完了${fail > 0 ? `（${fail} 件失敗）` : ''}`,
      );
    } catch (e) {
      toast('error', `一括操作失敗: ${(e as Error).message}`);
    } finally {
      setBulkBusy(false);
    }
  }

  const allSelected = currentItems.length > 0 && selectedIds.size === currentItems.length;

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← 地区詳細
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          ECW モデレーション
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          REQ-4.3.42 — 気づきカード (Insight) / 処方箋カード (Idea) の公開・非公開 / 削除。
        </p>
      </header>

      {!canModerate && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区の ECW モデレーション権限がありません (role: {authState.role ?? 'none'}、地区:{' '}
            {districtId})。
          </p>
        </Card>
      )}

      {modState.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {modState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {modState.error.message}</p>
        </Card>
      )}

      {modState.status === 'ready' && (
        <>
          <div
            className="mb-4 flex flex-wrap gap-2 items-center"
            role="tablist"
            aria-label="ECW カード種別"
          >
            {(
              [
                { id: 'insights', label: `気づきカード (${insights.length})` },
                { id: 'ideas', label: `処方箋カード (${ideas.length})` },
              ] as const
            ).map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.id)}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                    active
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="mb-4 flex flex-wrap gap-2 items-center">
            <span className="text-sm text-slate-600 mr-1">フィルタ:</span>
            {(
              [
                { id: 'all', label: 'すべて' },
                { id: 'visible', label: '公開のみ' },
                { id: 'hidden', label: '非公開のみ' },
              ] as const
            ).map((f) => {
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                    active
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
            <span className="text-sm text-slate-500 ml-2">{currentItems.length} 件</span>
          </div>

          {/* AW10 High: 全選択 (N/M) チェックボックス (old2 一括操作バー踏襲) */}
          {canModerate && currentItems.length > 0 && (
            <label className="mb-3 inline-flex items-center gap-1.5 text-sm cursor-pointer text-slate-700">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="w-4 h-4"
                aria-label="全選択"
              />
              全選択 ({selectedIds.size}/{currentItems.length})
            </label>
          )}

          {tab === 'insights' && (
            <InsightsTable
              items={filteredInsights}
              canModerate={canModerate}
              busyId={busyId}
              selectedIds={selectedIds}
              expandedId={expandedId}
              onToggleSelect={toggleSelect}
              onToggleExpand={toggleExpand}
              onToggle={handleToggleInsight}
              onDelete={handleDeleteInsight}
            />
          )}

          {tab === 'ideas' && (
            <IdeasTable
              items={filteredIdeas}
              canModerate={canModerate}
              busyId={busyId}
              selectedIds={selectedIds}
              expandedId={expandedId}
              onToggleSelect={toggleSelect}
              onToggleExpand={toggleExpand}
              onToggle={handleToggleIdea}
              onDelete={handleDeleteIdea}
            />
          )}

          {/* AW10 High: 一括操作バー (sticky bottom、shared-ui BulkActionsBar) */}
          {canModerate && (
            <BulkActionsBar
              selectedCount={selectedIds.size}
              busy={bulkBusy}
              itemLabel="件"
              onClear={() => setSelectedIds(new Set())}
              actions={[
                {
                  id: 'show',
                  label: '公開に戻す',
                  variant: 'secondary',
                  onClick: () => void handleBulk('show'),
                },
                {
                  id: 'hide',
                  label: '非公開にする',
                  variant: 'secondary',
                  onClick: () => void handleBulk('hide'),
                },
                {
                  id: 'delete',
                  label: '削除',
                  variant: 'danger',
                  onClick: () => void handleBulk('delete'),
                },
              ]}
            />
          )}
        </>
      )}
    </>
  );
}

/** emotionTag / categoryLabel バッジ + llmAnalysis 有無バッジ (AW10 Medium、old2 InsightRow/IdeaRow 踏襲)。 */
function MetaBadges({ item }: { item: InsightModerationItem | IdeaModerationItem }) {
  return (
    <>
      {item.emotionTag && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
          {item.emotionTag}
        </span>
      )}
      {item.categoryLabel && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
          {item.categoryLabel}
        </span>
      )}
      {item.llmAnalysis && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
          AI分析あり
        </span>
      )}
    </>
  );
}

interface InsightsTableProps {
  items: InsightModerationItem[];
  canModerate: boolean;
  busyId: string | null;
  selectedIds: Set<string>;
  expandedId: string | null;
  onToggleSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onToggle: (item: InsightModerationItem) => void;
  onDelete: (item: InsightModerationItem) => void;
}

function InsightsTable({
  items,
  canModerate,
  busyId,
  selectedIds,
  expandedId,
  onToggleSelect,
  onToggleExpand,
  onToggle,
  onDelete,
}: InsightsTableProps) {
  if (items.length === 0) {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-600">該当する気づきカードはありません。</p>
      </Card>
    );
  }
  return (
    <ul className="grid grid-cols-1 gap-2">
      {items.map((i) => {
        const selected = selectedIds.has(i.id);
        const expanded = expandedId === i.id;
        return (
          <li key={i.id}>
            <Card
              padding="md"
              className={`flex items-start gap-3 ${selected ? 'border-primary-500 ring-1 ring-primary-300' : ''} ${i.hidden ? 'opacity-70' : ''}`}
            >
              {canModerate && (
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggleSelect(i.id)}
                  className="w-4 h-4 mt-1 shrink-0"
                  aria-label={`「${i.title || '(無題)'}」を選択`}
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      i.hidden ? 'bg-danger-700 text-white' : 'bg-success-600 text-white'
                    }`}
                  >
                    {i.hidden ? '非公開' : '公開'}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                    sentiment {i.sentiment.toFixed(2)}
                  </span>
                  <MetaBadges item={i} />
                </div>
                <button
                  type="button"
                  onClick={() => onToggleExpand(i.id)}
                  className="mt-1 block text-left text-sm font-bold text-slate-900 hover:underline truncate w-full"
                  aria-expanded={expanded}
                >
                  {i.title || '(無題)'}
                </button>
                <p className="mt-1 text-[11px] text-slate-400">
                  {i.nickname}
                  {i.team ? ` ・ ${i.team}` : ''} ・{' '}
                  {i.createdAt ? i.createdAt.toLocaleString('ja-JP') : '日時不明'}
                </p>
                {!expanded && (
                  <>
                    {i.observations && (
                      <p className="mt-1 text-xs text-slate-600 line-clamp-2 whitespace-pre-wrap">
                        症状: {i.observations}
                      </p>
                    )}
                    {i.diagnosis && (
                      <p className="mt-1 text-xs text-slate-600 line-clamp-2 whitespace-pre-wrap">
                        診断: {i.diagnosis}
                      </p>
                    )}
                  </>
                )}
                {/* AW10 High: 行展開で全文表示 (whitespace-pre-wrap、old2 expanded ブロック踏襲) */}
                {expanded && (
                  <div className="mt-2 pt-2 border-t border-slate-100 text-sm space-y-2">
                    <div>
                      <strong className="text-slate-500 text-xs">症状:</strong>
                      <p className="whitespace-pre-wrap mt-0.5 text-slate-800">{i.observations}</p>
                    </div>
                    <div>
                      <strong className="text-slate-500 text-xs">診断:</strong>
                      <p className="whitespace-pre-wrap mt-0.5 text-slate-800">{i.diagnosis}</p>
                    </div>
                    {i.llmAnalysis && (
                      <div>
                        <strong className="text-slate-500 text-xs">AI分析:</strong>
                        <p className="whitespace-pre-wrap mt-0.5 text-slate-600">{i.llmAnalysis}</p>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-400">
                      docID: <code className="text-[10px]">{i.id}</code> ・ 紐づく投稿:{' '}
                      {i.selectedPostIds.length} 件 ・ uid:{' '}
                      <code className="text-[10px]">{i.userId}</code>
                    </p>
                  </div>
                )}
              </div>
              {canModerate && (
                <div className="shrink-0 flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onToggle(i)}
                    disabled={busyId === i.id}
                  >
                    {i.hidden ? '公開に戻す' : '非公開にする'}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => onDelete(i)}
                    disabled={busyId === i.id}
                  >
                    削除
                  </Button>
                </div>
              )}
            </Card>
          </li>
        );
      })}
    </ul>
  );
}

interface IdeasTableProps {
  items: IdeaModerationItem[];
  canModerate: boolean;
  busyId: string | null;
  selectedIds: Set<string>;
  expandedId: string | null;
  onToggleSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onToggle: (item: IdeaModerationItem) => void;
  onDelete: (item: IdeaModerationItem) => void;
}

function IdeasTable({
  items,
  canModerate,
  busyId,
  selectedIds,
  expandedId,
  onToggleSelect,
  onToggleExpand,
  onToggle,
  onDelete,
}: IdeasTableProps) {
  if (items.length === 0) {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-600">該当する処方箋カードはありません。</p>
      </Card>
    );
  }
  return (
    <ul className="grid grid-cols-1 gap-2">
      {items.map((i) => {
        const selected = selectedIds.has(i.id);
        const expanded = expandedId === i.id;
        return (
          <li key={i.id}>
            <Card
              padding="md"
              className={`flex items-start gap-3 ${selected ? 'border-primary-500 ring-1 ring-primary-300' : ''} ${i.hidden ? 'opacity-70' : ''}`}
            >
              {canModerate && (
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggleSelect(i.id)}
                  className="w-4 h-4 mt-1 shrink-0"
                  aria-label={`「${i.title || '(無題)'}」を選択`}
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      i.hidden ? 'bg-danger-700 text-white' : 'bg-success-600 text-white'
                    }`}
                  >
                    {i.hidden ? '非公開' : '公開'}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">
                    insights: {i.insightIds.length}
                  </span>
                  <MetaBadges item={i} />
                </div>
                <button
                  type="button"
                  onClick={() => onToggleExpand(i.id)}
                  className="mt-1 block text-left text-sm font-bold text-slate-900 hover:underline truncate w-full"
                  aria-expanded={expanded}
                >
                  {i.title || '(無題)'}
                </button>
                <p className="mt-1 text-[11px] text-slate-400">
                  {i.nickname}
                  {i.team ? ` ・ ${i.team}` : ''} ・{' '}
                  {i.createdAt ? i.createdAt.toLocaleString('ja-JP') : '日時不明'}
                </p>
                {!expanded && (
                  <>
                    {i.observations && (
                      <p className="mt-1 text-xs text-slate-600 line-clamp-2 whitespace-pre-wrap">
                        症状・気づき: {i.observations}
                      </p>
                    )}
                    {i.actionIdea && (
                      <p className="mt-1 text-xs text-slate-600 line-clamp-2 whitespace-pre-wrap">
                        処方箋: {i.actionIdea}
                      </p>
                    )}
                  </>
                )}
                {/* AW10 High: 行展開で全文表示 (whitespace-pre-wrap、old2 expanded ブロック踏襲) */}
                {expanded && (
                  <div className="mt-2 pt-2 border-t border-slate-100 text-sm space-y-2">
                    <div>
                      <strong className="text-slate-500 text-xs">症状・気づき:</strong>
                      <p className="whitespace-pre-wrap mt-0.5 text-slate-800">{i.observations}</p>
                    </div>
                    <div>
                      <strong className="text-slate-500 text-xs">処方・アクション:</strong>
                      <p className="whitespace-pre-wrap mt-0.5 text-slate-800">{i.actionIdea}</p>
                    </div>
                    {i.llmAnalysis && (
                      <div>
                        <strong className="text-slate-500 text-xs">AI分析:</strong>
                        <p className="whitespace-pre-wrap mt-0.5 text-slate-600">{i.llmAnalysis}</p>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-400">
                      docID: <code className="text-[10px]">{i.id}</code> ・ 関連する気づき:{' '}
                      {i.insightIds.length} 件 ・ 紐づく投稿: {i.selectedPostIds.length} 件 ・ uid:{' '}
                      <code className="text-[10px]">{i.userId}</code>
                    </p>
                  </div>
                )}
              </div>
              {canModerate && (
                <div className="shrink-0 flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onToggle(i)}
                    disabled={busyId === i.id}
                  >
                    {i.hidden ? '公開に戻す' : '非公開にする'}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => onDelete(i)}
                    disabled={busyId === i.id}
                  >
                    削除
                  </Button>
                </div>
              )}
            </Card>
          </li>
        );
      })}
    </ul>
  );
}
